/**
 * The live structure-set facts the structure-management ops read: the
 * same values the structure list and the settings panel show, read at call
 * time (not render time) so a re-check at Apply and the read-back after it
 * see the current state. Ports only — the engine adapter fills them from
 * the session's structure set (`createStructureSetReadModel`).
 */
import { resolveRoi } from '../core/roiResolver';
import {
    failedPlan,
    resolveFailureToPlan,
    toListed,
    type MutatingPlan,
    type PendingFlag,
    type StructureFacts,
} from '../core';

/** Position in DICOM patient coordinates, mm. */
export type PointMm = readonly [x: number, y: number, z: number];

export interface LiveRoi {
    readonly roiNumber: number;
    readonly name: string;
    /** DICOM RT ROI Interpreted Type, '' when unknown. */
    readonly type: string;
    /** 'UNAPPROVED' | 'APPROVED' | 'REJECTED' */
    readonly approvalStatus: string;
    readonly isApproved: boolean;
    readonly isPointRoi: boolean;
    readonly pointMm?: PointMm;
}

export interface LiveStructureSet {
    readonly uid: string;
    readonly label: string;
    readonly description: string;
    /** 'UNAPPROVED' | 'APPROVED' | 'REJECTED' */
    readonly approvalStatus: string;
    readonly rois: ReadonlyArray<LiveRoi>;
}

export interface LiveDeps extends PendingFlag {
    /** Resolution facts (names, types, volumes, emptiness). */
    readonly structures: ReadonlyArray<StructureFacts>;
    /** The active structure set as it is now; undefined when none. */
    liveSet(): LiveStructureSet | undefined;
    /** Identity of the active structure set, read live. */
    currentContextId(): string | undefined;
}

export const liveRoi = (
    deps: Pick<LiveDeps, 'liveSet'>,
    roiNumber: number
): LiveRoi | undefined =>
    deps.liveSet()?.rois.find((r) => r.roiNumber === roiNumber);

export const approvedReason = (name: string): string =>
    `'${name}' is approved and cannot be changed`;

/** Resolve a structure by exact name, any kind (empty and markers too). */
export function resolveAnyRoi(
    toolCallId: string,
    roi: unknown,
    deps: Pick<LiveDeps, 'structures' | 'liveSet'>
):
    | {
          readonly kind: 'ok';
          readonly roi: LiveRoi;
          readonly facts?: StructureFacts;
      }
    | Extract<MutatingPlan<never>, { kind: 'recoverable' }> {
    if (typeof roi !== 'string' || !roi) {
        return failedPlan(toolCallId, 'a structure name is required');
    }
    const resolution = resolveRoi(roi, deps.structures.map(toListed));
    if (resolution.kind !== 'resolved') {
        return resolveFailureToPlan(
            resolution.kind === 'ambiguous'
                ? {
                      kind: 'ambiguous',
                      reason: resolution.reason,
                      candidates: resolution.candidates,
                  }
                : { kind: 'not-found', suggestions: resolution.suggestions },
            toolCallId
        );
    }
    const n = resolution.structure.roiNumber;
    const live = liveRoi(deps, n);
    if (!live) {
        return failedPlan(toolCallId, `'${roi}' is no longer loaded`);
    }
    const facts = deps.structures.find((s) => s.roiNumber === n);
    return { kind: 'ok', roi: live, ...(facts ? { facts } : {}) };
}
